<script setup>
import { reactiveOmit } from "@vueuse/core";
import { CalendarRoot, useForwardPropsEmits } from "reka-ui";
import { cn } from "@/shared/lib/utils";
import { weekdayLabels, monthYearLabel } from "./locale-labels";
import CalendarCell from "./CalendarCell.vue";
import CalendarCellTrigger from "./CalendarCellTrigger.vue";
import CalendarGrid from "./CalendarGrid.vue";
import CalendarGridBody from "./CalendarGridBody.vue";
import CalendarGridHead from "./CalendarGridHead.vue";
import CalendarGridRow from "./CalendarGridRow.vue";
import CalendarHeadCell from "./CalendarHeadCell.vue";
import CalendarHeader from "./CalendarHeader.vue";
import CalendarHeading from "./CalendarHeading.vue";
import CalendarNextButton from "./CalendarNextButton.vue";
import CalendarPrevButton from "./CalendarPrevButton.vue";

const props = defineProps({
  modelValue: { type: Object, default: undefined },
  defaultValue: { type: Object, default: undefined },
  defaultPlaceholder: { type: Object, default: undefined },
  placeholder: { type: Object, default: undefined },
  pagedNavigation: { type: Boolean, default: false },
  preventDeselect: { type: Boolean, default: false },
  weekStartsOn: { type: Number, default: 1 },
  weekdayFormat: { type: String, default: "short" },
  calendarLabel: { type: String, default: undefined },
  fixedWeeks: { type: Boolean, default: false },
  maxValue: { type: Object, default: undefined },
  minValue: { type: Object, default: undefined },
  locale: { type: String, default: "ru" },
  numberOfMonths: { type: Number, default: 1 },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  initialFocus: { type: Boolean, default: false },
  isDateDisabled: { type: Function, default: undefined },
  isDateUnavailable: { type: Function, default: undefined },
  multiple: { type: Boolean, default: false },
  class: { type: String, default: "" },
});

const emits = defineEmits(["update:modelValue", "update:placeholder"]);

const delegatedProps = reactiveOmit(props, "class");
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <CalendarRoot
    v-slot="{ grid }"
    :class="cn('p-3', props.class)"
    v-bind="forwarded"
  >
    <CalendarHeader>
      <CalendarPrevButton />
      <CalendarHeading>
        {{
          monthYearLabel(
            props.locale,
            grid?.[0]?.value?.month,
            grid?.[0]?.value?.year
          )
        }}
      </CalendarHeading>
      <CalendarNextButton />
    </CalendarHeader>
    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid v-for="month in grid" :key="month.value.toString()">
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="(day, i) in weekdayLabels(props.locale)"
              :key="i"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="mt-2 w-full"
          >
            <CalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
            >
              <CalendarCellTrigger :day="weekDate" :month="month.value" />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>
